import { _decorator, Component, ScrollView, UITransform, Node, TransformBit, director, Director, v2, clamp, Enum, v4 } from 'cc';
import { DEV } from 'cc/env';
import ItemGenerator from '../Misc/ItemGenerator';

const { ccclass, property, requireComponent } = _decorator;

enum EventType {
    FIRST_ITEM_INDEX_CHANGED = 'FIRST_ITEM_INDEX_CHANGED',
    ITEM_COUNT_CHANGED = 'ITEM_COUNT_CHANGED',
}

enum AxisDirection {
    HORIZONTAL = 0,
    VERTICAL = 1,
}

enum Constraint {
    NONE = 0,
    FIXED_ROW = 1,
    FIXED_COL = 2,
}

enum LayoutType {
    HORIZONTAL,
    VERTICAL,
    GRID,
}

enum PositionType {
    FRONT,
    MIDDLE,
    BACK,
}

@ccclass('InfiniteScrollView')
@requireComponent(ScrollView)
export default class InfiniteScrollView extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property({ type: Enum(PositionType), tooltip: DEV && '位置選項 (ScrollView 的前中後)' })
    protected readonly defaultPosition: PositionType = PositionType.FRONT;

    @property({ step: 1, min: 0, tooltip: DEV && '設定該預設 Index 的位置' })
    protected readonly defaultIndex = 0;

    @property
    private _maxVisibleRowCount = 4;

    @property
    private _maxVisibleColCount = 4;

    @property({ formerlySerializedAs: 'maxVisibleCount' })
    private _maxVisibleCount = 10;

    @property({
        formerlySerializedAs: 'paddingTop',
    })
    private _paddingTop = 0;

    @property({ formerlySerializedAs: 'paddingBottom' })
    private _paddingBottom = 0;

    @property({ formerlySerializedAs: 'spacingY' })
    private _spacingY = 0;

    @property({ formerlySerializedAs: 'itemHeight' })
    private _itemHeight = 0;

    @property({ formerlySerializedAs: 'paddingLeft' })
    private _paddingLeft = 0;

    @property({ formerlySerializedAs: 'paddingRight' })
    private _paddingRight = 0;

    @property({ formerlySerializedAs: 'spacingX' })
    private _spacingX = 0;

    @property({ formerlySerializedAs: 'itemWidth' })
    private _itemWidth = 0;

    /**
     * @deprecated Please use layoutType instead.
     */
    @property({ formerlySerializedAs: 'horizontal' })
    private readonly _horizontal = false;

    @property
    private _needMigrate = true;

    @property
    private _layoutType: LayoutType = LayoutType.VERTICAL;

    @property
    private _startAxis: AxisDirection = AxisDirection.HORIZONTAL;

    @property
    private _constraint: Constraint = Constraint.NONE;

    @property
    private _constraintNum: number = 2;

    protected contentAdditionalOffset = v2(0, 0);
    private _scrollView: ScrollView = null;
    private _contentUITransform: UITransform = null;
    private _itemGenerator: ItemGenerator = null;
    private _view: Node = null;

    private _itemCount = 0;
    private _firstItemIndex = 0;
    private _firstItemRowIndex = 0;
    private _firstItemColIndex = 0;
    private _contentPositionDirty = false;

    @property({
        type: Enum(LayoutType),
    })
    public get layoutType() {
        if (this._needMigrate) {
            return this._horizontal ? LayoutType.HORIZONTAL : LayoutType.VERTICAL;
        }
        return this._layoutType;
    }
    public set layoutType(value: LayoutType) {
        if (this._layoutType === value) {
            return;
        }
        this._layoutType = value;
        // if layoutType is set, means old horizontal property is not used
        this._needMigrate = false;
    }

    @property({
        tooltip: DEV && 'row major or column major',
        type: Enum(AxisDirection),
        visible(this: InfiniteScrollView) {
            return !(this.isHorizontal || this.isVertical);
        },
    })
    public get startAxis() {
        return this._startAxis;
    }
    public set startAxis(value: AxisDirection) {
        if (this._startAxis === value) {
            return;
        }
        this._startAxis = value;
    }

    @property({
        min: 1,
        step: 1,
        visible(this: InfiniteScrollView) {
            return !(this.isHorizontal || this.isVertical);
        },
    })
    public get maxVisibleRowCount() {
        return this._maxVisibleRowCount;
    }
    public set maxVisibleRowCount(value: number) {
        if (value <= 0) {
            throw new Error('maxVisibleRowCount must be greater than 0.');
        }
        if (this._maxVisibleRowCount === value) {
            return;
        }
        this._maxVisibleRowCount = value;
    }

    @property({
        min: 1,
        step: 1,
        visible(this: InfiniteScrollView) {
            return !(this.isHorizontal || this.isVertical);
        },
    })
    public get maxVisibleColCount() {
        return this._maxVisibleColCount;
    }
    public set maxVisibleColCount(value: number) {
        if (value <= 0) {
            throw new Error('maxVisibleColCount must be greater than 0.');
        }
        if (this._maxVisibleColCount === value) {
            return;
        }
        this._maxVisibleColCount = value;
    }

    @property({
        min: 1,
        step: 1,
        visible(this: InfiniteScrollView) {
            return this.isHorizontal || this.isVertical;
        },
    })
    public get maxVisibleCount() {
        return this._maxVisibleCount;
    }
    public set maxVisibleCount(value: number) {
        if (value <= 0) {
            throw new Error('maxVisibleCount must be greater than 0.');
        }
        if (this._maxVisibleCount === value) {
            return;
        }
        this._maxVisibleCount = value;
        // for backward compatibility with existing logic
        this.updateLayoutType(this.layoutType);
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isHorizontal;
        },
    })
    public get paddingTop() {
        return this._paddingTop;
    }
    public set paddingTop(value: number) {
        if (this._paddingTop === value) {
            return;
        }
        this._paddingTop = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isHorizontal;
        },
    })
    public get paddingBottom() {
        return this._paddingBottom;
    }
    public set paddingBottom(value: number) {
        if (this._paddingBottom === value) {
            return;
        }
        this._paddingBottom = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isHorizontal;
        },
    })
    public get spacingY() {
        return this._spacingY;
    }
    public set spacingY(value: number) {
        if (this._spacingY === value) {
            return;
        }
        this._spacingY = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isHorizontal;
        },
    })
    public get itemHeight() {
        return !this.isHorizontal ? this._itemHeight : 0;
    }
    public set itemHeight(value: number) {
        if (this._itemHeight === value) {
            return;
        }
        this._itemHeight = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isVertical;
        },
    })
    public get paddingLeft() {
        return this._paddingLeft;
    }
    public set paddingLeft(value: number) {
        if (this._paddingLeft === value) {
            return;
        }
        this._paddingLeft = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isVertical;
        },
    })
    public get paddingRight() {
        return this._paddingRight;
    }
    public set paddingRight(value: number) {
        if (this._paddingRight === value) {
            return;
        }
        this._paddingRight = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isVertical;
        },
    })
    public get spacingX() {
        return this._spacingX;
    }
    public set spacingX(value: number) {
        if (this._spacingX === value) {
            return;
        }
        this._spacingX = value;
    }

    @property({
        visible(this: InfiniteScrollView) {
            return !this.isVertical;
        },
    })
    public get itemWidth() {
        return !this.isVertical ? this._itemWidth : 0;
    }
    public set itemWidth(value: number) {
        if (this._itemWidth === value) {
            return;
        }
        this._itemWidth = value;
    }

    @property({
        type: Enum(Constraint),
        visible(this: InfiniteScrollView) {
            return !(this.isHorizontal || this.isVertical);
        },
    })
    public get constraint() {
        return this._constraint;
    }
    public set constraint(value: Constraint) {
        if (this._constraint === value) {
            return;
        }
        this._constraint = value;
    }

    @property({
        min: 1,
        step: 1,
        visible(this: InfiniteScrollView) {
            return !(this.isHorizontal || this.isVertical) && this.constraint !== Constraint.NONE;
        },
    })
    public get constraintNum() {
        return this.layoutType !== LayoutType.GRID || this.constraint === Constraint.NONE ? 1 : this._constraintNum;
    }
    public set constraintNum(value: number) {
        if (value <= 0) {
            throw new Error('constraintNum must be greater than 0.');
        }
        if (this._constraintNum === value) {
            return;
        }
        this._constraintNum = value;
    }

    public get scrollView() {
        if (!this._scrollView) {
            this._scrollView = this.getComponent(ScrollView);
        }
        return this._scrollView;
    }

    public get itemGenerator() {
        if (!this._itemGenerator) {
            this._itemGenerator = this.scrollView.content.getComponent(ItemGenerator);
        }
        return this._itemGenerator;
    }

    public get firstItemIndex() {
        return this._firstItemIndex;
    }
    private set firstItemIndex(value: number) {
        if (value === this._firstItemIndex) {
            return;
        }
        this._firstItemIndex = value;
        this._updateContentLayout();
        this.node.emit(EventType.FIRST_ITEM_INDEX_CHANGED);
    }

    public get itemCount() {
        return this._itemCount;
    }
    public set itemCount(value: number) {
        if (value < 0) {
            throw new Error('itemCount must be greater than or equal to 0.');
        }
        if (this._itemCount === value) {
            return;
        }
        this._itemCount = value;
        this.updateLayout();
        this.node.emit(EventType.ITEM_COUNT_CHANGED);
    }

    private get isHorizontal() {
        if (this._needMigrate) {
            return this._horizontal;
        }
        return this.layoutType === LayoutType.HORIZONTAL;
    }

    private get isVertical() {
        if (this._needMigrate) {
            return !this._horizontal;
        }
        return this.layoutType === LayoutType.VERTICAL;
    }

    private get view() {
        if (!this._view) {
            this._view = this.scrollView.content.parent;
        }
        return this._view;
    }

    private get contentUITransform() {
        if (!this._contentUITransform) {
            this._contentUITransform = this.scrollView.content.transform;
        }
        return this._contentUITransform;
    }

    private get horizontalItemCount() {
        return this.currentConstraint === Constraint.FIXED_ROW ? Math.ceil(this.itemCount / this.constraintNum) : Math.min(this.itemCount, this.constraintNum);
    }

    private get verticalItemCount() {
        return this.currentConstraint === Constraint.FIXED_ROW ? Math.min(this.itemCount, this.constraintNum) : Math.ceil(this.itemCount / this.constraintNum);
    }

    private get currentConstraint() {
        if (this.isHorizontal) {
            return Constraint.FIXED_ROW;
        }
        if (this.isVertical) {
            return Constraint.FIXED_COL;
        }
        const defaultConstraint = this.startAxis === AxisDirection.HORIZONTAL ? Constraint.FIXED_ROW : Constraint.FIXED_COL;
        return this.constraint === Constraint.NONE ? defaultConstraint : this.constraint;
    }

    private get actualMaxVisibleRowCount() {
        return Math.min(this.currentConstraint === Constraint.FIXED_ROW ? this.constraintNum : Number.MAX_SAFE_INTEGER, this.maxVisibleRowCount);
    }

    private get actualMaxVisibleColCount() {
        return Math.min(this.currentConstraint === Constraint.FIXED_COL ? this.constraintNum : Number.MAX_SAFE_INTEGER, this.maxVisibleColCount);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public __preload(): void {
        this.defaultSetting();
    }

    public onLoad() {
        this.on(InfiniteScrollView.EVENT_TYPE.FIRST_ITEM_INDEX_CHANGED, this._checkNodeActive, this);
        this.itemGenerator.on(Node.EventType.CHILD_ADDED, this._adjustItemPosition, this);
        this.itemGenerator.on(Node.EventType.SIBLING_ORDER_CHANGED, this._updateContentLayout, this);
        this.scrollView.content.on(Node.EventType.TRANSFORM_CHANGED, (transformBit: TransformBit) => {
            if ((transformBit as number) & TransformBit.POSITION) {
                this._contentPositionDirty = true;
            }
        });
        director.on(Director.EVENT_AFTER_UPDATE, this.syncFirstItemIndex, this);

        if (this.itemCount > 0) {
            switch (this.defaultPosition) {
                case PositionType.FRONT:
                    this.moveTo(this.defaultIndex);
                    break;
                case PositionType.MIDDLE:
                    this.moveToMiddle(this.defaultIndex);
                    break;
                case PositionType.BACK:
                    this.moveFromBack(this.defaultIndex);
                    break;
                default:
                    this.moveTo(this.defaultIndex);
            }
        }
    }

    public updateLayoutType(type: LayoutType) {
        if (type === LayoutType.HORIZONTAL) {
            this._maxVisibleRowCount = 1;
            this._maxVisibleColCount = this._maxVisibleCount;
        } else if (type === LayoutType.VERTICAL) {
            this._maxVisibleRowCount = this._maxVisibleCount;
            this._maxVisibleColCount = 1;
        } else {
            this._maxVisibleCount = this.maxVisibleColCount * this.maxVisibleRowCount;
        }
    }

    public resetInEditor() {
        // new added component do not need migrate
        this._needMigrate = false;
    }

    // 輸入畫面上 node 的 index，回傳真正的 Index
    public getNodeIndex(viewIndex: number) {
        const [itemRowIndex, itemColIndex] = this._getRowAndColIndexOnView(viewIndex);
        return this.currentConstraint === Constraint.FIXED_ROW
            ? itemColIndex * this.constraintNum + itemRowIndex
            : itemRowIndex * this.constraintNum + itemColIndex;
    }

    // 使用時，掛載 InfinityScrollView 的 node 延伸方向的 anchor 請使用 0/1 (horizontal -> anchor.X / vertical -> anchor.Y)
    public moveTo(itemIndex: number, time?: number, attenuated: boolean = true) {
        if (!this._checkMoveability()) {
            return;
        }

        let ax = 0;
        let ay = 0;
        const [rowIndex, colIndex] = this._getRowColByItemIndex(itemIndex);

        if (itemIndex > this.itemCount) {
            itemIndex = this.itemCount - 1;
        }

        if (!this.isVertical) {
            ax =
                ((this.itemWidth + this.spacingX) * colIndex +
                    (this.spacingX * colIndex) / Math.max(this.horizontalItemCount - (this.maxVisibleColCount - 2), 1)) /
                (this.contentUITransform.width + this.spacingX - this.view.transformWidth);
        }
        if (!this.isHorizontal) {
            ay =
                ((this.itemHeight + this.spacingY) * rowIndex +
                    (this.spacingY * rowIndex) / Math.max(this.verticalItemCount - (this.maxVisibleRowCount - 2), 1)) /
                (this.contentUITransform.height + this.spacingY - this.view.transformHeight);
        }
        this._scrollTo(ax, ay, time, attenuated);
    }

    // 使用時，掛載 InfinityScrollView 的 node 延伸方向的 anchor 請使用 0/1 (horizontal -> anchor.X / vertical -> anchor.Y)
    public moveFromBack(itemIndex: number, time: number = 0, attenuated: boolean = true) {
        if (!this._checkMoveability()) {
            return;
        }

        let ax = 0;
        let ay = 0;
        const [rowIndex, colIndex] = this._getRowColByItemIndex(itemIndex);

        if (itemIndex > this.itemCount) {
            itemIndex = this.itemCount - 1;
        }

        if (!this.isVertical) {
            const lastCol = this.horizontalItemCount - 1;
            ax =
                1 -
                ((this.itemWidth + this.spacingX) * (lastCol - colIndex) +
                    (this.spacingX * Math.max(lastCol - colIndex, 1)) / (lastCol - (this.maxVisibleRowCount - 2))) /
                    (this.contentUITransform.width + this.spacingX - this.view.transformWidth);
        }
        if (!this.isHorizontal) {
            const lastRow = this.verticalItemCount - 1;
            ay =
                1 -
                ((this.itemHeight + this.spacingY) * (lastRow - rowIndex) +
                    (this.spacingY * Math.max(lastRow - rowIndex, 1)) / (lastRow - (this.maxVisibleRowCount - 2))) /
                    (this.contentUITransform.height + this.spacingY - this.view.transformHeight);
        }
        this._scrollTo(ax, ay, time, attenuated);
    }

    public moveToMiddle(itemIndex: number, time: number = 0, attenuated: boolean = true) {
        if (!this._checkMoveability()) {
            return;
        }

        let ax = 0;
        let ay = 0;
        const [rowIndex, colIndex] = this._getRowColByItemIndex(itemIndex);

        if (itemIndex > this.itemCount) {
            itemIndex = this.itemCount - 1;
        }

        if (!this.isVertical) {
            ax =
                ((this.itemWidth + this.spacingX) * colIndex +
                    (this.spacingX * colIndex) / Math.max(this.horizontalItemCount - (this.maxVisibleColCount - 2), 1) -
                    this.view.transformWidth / 2 +
                    this.itemWidth / 2) /
                (this.contentUITransform.width + this.spacingX - this.view.transformWidth);
        }
        if (!this.isHorizontal) {
            ay =
                ((this.itemHeight + this.spacingY) * rowIndex +
                    (this.spacingY * rowIndex) / Math.max(this.verticalItemCount - (this.maxVisibleRowCount - 2), 1) -
                    this.view.transformHeight / 2 +
                    this.itemHeight / 2) /
                (this.contentUITransform.height + this.spacingY - this.view.transformHeight);
        }
        this._scrollTo(ax, ay, time, attenuated);
    }

    public syncFirstItemIndex(force: boolean = false) {
        if (!this._contentPositionDirty && !force) {
            return;
        }
        this._contentPositionDirty = false;
        // Anchor-aware calculation for first visible row/col
        const view = this.view.transform;
        const contentTop = this.scrollView.content.y + (1 - this.contentUITransform.anchorY) * this.contentUITransform.height;
        const viewTop = (1 - view.anchorY) * view.height;
        const contentLeft = this.scrollView.content.x - this.contentUITransform.anchorX * this.contentUITransform.width;
        const viewLeft = -view.anchorX * view.width;

        this._firstItemColIndex = 0;
        this._firstItemRowIndex = 0;
        if (!this.isHorizontal) {
            // Rows: measured from content top downward to view top
            this._firstItemRowIndex = Math.max(0, Math.trunc((contentTop - viewTop - this.paddingTop) / (this.itemHeight + this.spacingY)));
        }
        if (!this.isVertical) {
            // Cols: measured from content left rightward to view left
            this._firstItemColIndex = Math.max(0, Math.trunc((viewLeft - contentLeft - this.paddingLeft) / (this.itemWidth + this.spacingX)));
        }
        this.firstItemIndex =
            this.currentConstraint === Constraint.FIXED_ROW
                ? this._firstItemColIndex * this.constraintNum + this._firstItemRowIndex
                : this._firstItemRowIndex * this.constraintNum + this._firstItemColIndex;
    }

    public updateLayout() {
        // 因為 itemGenerator 會依照 displayItemCount 生成 node，所以要先設定 displayItemCount 再更新 contentSize
        this.itemGenerator.displayItemCount = Math.min(this.actualMaxVisibleRowCount * this.actualMaxVisibleColCount, this.itemCount);
        this._setContentSize();
        this._checkNodeActive();
        this._updateContentLayout();
    }

    protected defaultSetting() {
        this.updateLayoutType(this.layoutType);
        this._setContentSize();
        this._updateContentLayout();
    }

    private _getRowAndColIndexOnView(nodeIndex: number) {
        const innerWidth = this.actualMaxVisibleColCount;
        const innerHeight = this.actualMaxVisibleRowCount;
        const itemRowIndex =
            this._firstItemRowIndex + (this.currentConstraint === Constraint.FIXED_ROW ? nodeIndex % innerHeight : Math.floor(nodeIndex / innerWidth));
        const itemColIndex =
            this._firstItemColIndex + (this.currentConstraint === Constraint.FIXED_ROW ? Math.floor(nodeIndex / innerHeight) : nodeIndex % innerWidth);
        return [itemRowIndex, itemColIndex];
    }

    private _getRowColByItemIndex(itemIndex: number): [number, number] {
        const rowIndex = this.currentConstraint === Constraint.FIXED_ROW ? itemIndex % this.constraintNum : Math.floor(itemIndex / this.constraintNum);
        const colIndex = this.currentConstraint === Constraint.FIXED_ROW ? Math.floor(itemIndex / this.constraintNum) : itemIndex % this.constraintNum;
        return [rowIndex, colIndex];
    }

    private _scrollTo(targetAnchorX: number, targetAnchorY: number, time: number, attenuated: boolean) {
        let diffX = 0;
        let diffY = 0;
        if (!this.isHorizontal) {
            const ay = clamp(targetAnchorY, 0, 1);
            diffY = Math.abs(1 - ay);
        }
        if (!this.isVertical) {
            const ax = clamp(targetAnchorX, 0, 1);
            diffX = ax;
        }
        this.scrollView.scrollTo(v2(diffX, diffY), time, attenuated);
    }

    private _updateContentLayout() {
        this.scrollView.content.children.forEach((node) => {
            this._adjustItemPosition(node);
        });
    }

    private _setContentSize() {
        const contentSizeX = this.paddingLeft + this.paddingRight + this.horizontalItemCount * (this.itemWidth + this.spacingX) - this.spacingX;
        const contentSizeY = this.paddingTop + this.paddingBottom + this.verticalItemCount * (this.itemHeight + this.spacingY) - this.spacingY;
        this.contentUITransform.setContentSize(contentSizeX, contentSizeY);
    }

    private _adjustItemPosition(node: Node) {
        const index = node.getSiblingIndex();
        const [itemRowIndex, itemColIndex] = this._getRowAndColIndexOnView(index);
        node.x =
            this.paddingLeft +
            this.itemWidth / 2 +
            itemColIndex * (this.itemWidth + this.spacingX) -
            (!this.isVertical ? this.contentUITransform.anchorX * this.contentUITransform.width : 0);
        node.y =
            -(this.paddingTop + this.itemHeight / 2 + itemRowIndex * (this.itemHeight + this.spacingY)) +
            (!this.isHorizontal ? (1 - this.contentUITransform.anchorY) * this.contentUITransform.height : 0);
    }

    private _checkNodeActive() {
        // 原本處理上下或左右所以只需要判斷頭尾 -> 現在 grid 有可能上下左右都要判斷
        const outerWidth = this.currentConstraint === Constraint.FIXED_ROW ? Math.ceil(this.itemCount / this.constraintNum) : this.constraintNum;
        const outerHeight = this.currentConstraint === Constraint.FIXED_ROW ? this.constraintNum : Math.ceil(this.itemCount / this.constraintNum);
        const outerBoundingBoxIndex = v4(0, 0, outerWidth - 1, outerHeight - 1);
        const innerWidth = this.actualMaxVisibleColCount;
        const innerHeight = this.actualMaxVisibleRowCount;
        const innerBoundingBoxIndex = v4(
            this._firstItemColIndex,
            this._firstItemRowIndex,
            this._firstItemColIndex + innerWidth - 1,
            this._firstItemRowIndex + innerHeight - 1,
        );
        if (
            innerBoundingBoxIndex.x <= outerBoundingBoxIndex.x ||
            innerBoundingBoxIndex.y <= outerBoundingBoxIndex.y ||
            innerBoundingBoxIndex.z >= outerBoundingBoxIndex.z ||
            innerBoundingBoxIndex.w >= outerBoundingBoxIndex.w
        ) {
            this.itemGenerator.items.forEach((item, index) => {
                const [itemRowIndex, itemColIndex] = this._getRowAndColIndexOnView(index);
                item.opacity = !(
                    itemColIndex < outerBoundingBoxIndex.x ||
                    itemRowIndex < outerBoundingBoxIndex.y ||
                    itemColIndex > outerBoundingBoxIndex.z ||
                    itemRowIndex > outerBoundingBoxIndex.w ||
                    this.getNodeIndex(index) >= this.itemCount
                )
                    ? 255
                    : 0;
            });
        }
    }

    private _checkMoveability() {
        const { x: maxScrollOffsetX, y: maxScrollOffsetY } = this.scrollView.getMaxScrollOffset();
        if (maxScrollOffsetX === 0 && maxScrollOffsetY === 0) {
            return false;
        }
        if (this.isVertical && maxScrollOffsetY === 0) {
            return false;
        }
        if (this.isHorizontal && maxScrollOffsetX === 0) {
            return false;
        }
        return true;
    }
}
